/* eslint-disable @next/next/no-img-element */
import { useRef, useEffect, useState } from "react";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
const ReactCountryFlag = dynamic(() => import("react-country-flag"));

const SocialShare = dynamic(
  () => import("../../links/SocialLinks/SocialShare")
);
const Breadcrumbs = dynamic(
  () => import("../../ui/nav/Breadcrumbs/Breadcrumbs")
);
const FavoriteButton = dynamic(
  () => import("../../ui/buttons/FavoriteButton/FavoriteButton")
);

import { site } from "config";
import { insertTagsInCloudinaryUrl } from "utils/image";
import styles from "./Header.module.scss";

export default function Header({
  title,
  author,
  date,
  image,
  format,
  location,
  destination,
  socialShareConfig,
  screenSize,
  id,
}) {
  if (format === "image") {
    return (
      <ImageHeader
        title={title}
        author={author}
        date={date}
        image={image}
        format={format}
        location={location}
        destination={destination}
        screenSize={screenSize}
        socialShareConfig={socialShareConfig}
        id={id}
      />
    );
  }

  const destinationTitle =
    location?.country ??
    location?.destinations[0]?.destination_title ??
    location?.city ??
    location?.region;
  const country_short_code =
    location?.country_code ??
    location?.destinations[0]?.destination_details?.country_short_code;

  return (
    <TextHeader
      title={title}
      country_short_code={country_short_code}
      destinationTitle={destinationTitle}
      socialShareConfig={socialShareConfig}
      destination={destination}
    />
  );
}

function ImageHeader({
  title,
  author,
  date,
  image,
  screenSize,
  socialShareConfig,
  id,
}) {
  const [isImageBroken, setIsImageBroken] = useState(false);
  const { name, user } = author;
  const displayName = name?.split(" ")?.join(" ") || user?.first_name;

  const imageRef = useRef(null);

  const router = useRouter();

  useEffect(() => {
    if (!router.isReady) return;

    if (
      imageRef?.current &&
      imageRef?.current?.complete &&
      imageRef?.current?.naturalWidth === 0
    ) {
      setIsImageBroken(true);
    } else {
      setIsImageBroken(false);
    }
  }, [imageRef.current, router.isReady]);

  return (
    <header
      className={`${styles.imageHeader} ${!image || !!isImageBroken ? styles.noImage : ""}`}
    >
      <FavoriteButton favoriteType="travelGuides" objectId={id} />
      {image && !isImageBroken && (
        <div className={`${styles.imageWrapper}`}>
          <picture>
            <source
              srcSet={insertTagsInCloudinaryUrl(
                image,
                `c_auto,ar_8:7,g_auto,w_0.75,q_auto,f_auto`
              )}
              media="(max-width: 480px)"
            />
            <source
              srcSet={insertTagsInCloudinaryUrl(
                image,
                `c_auto,ar_64:35,g_auto,w_iw,q_auto,f_auto`
              )}
              media="(max-width: 768px)"
            />
            <source
              srcSet={insertTagsInCloudinaryUrl(
                image,
                `c_auto,ar_1.61,g_auto,w_iw,q_auto,f_auto`
              )}
              media="(max-width: 992)"
            />
            <source
              srcSet={insertTagsInCloudinaryUrl(
                image,
                `c_auto,ar_2.87,g_auto,w_iw,q_auto,f_auto`
              )}
              media="(max-width: 1280px)"
            />
            <source
              srcSet={insertTagsInCloudinaryUrl(
                image,
                `c_auto,ar_2.22,g_auto,w_iw,q_auto,f_auto`
              )}
              media="(max-width: 1366px)"
            />
            <source
              srcSet={insertTagsInCloudinaryUrl(
                image,
                `c_auto,ar_2.31,g_auto,w_iw,q_auto,f_auto`
              )}
              media="(max-width: 1440px)"
            />
            <source
              srcSet={insertTagsInCloudinaryUrl(
                image,
                `c_auto,ar_2.31,g_auto,w_iw,q_auto,f_auto`
              )}
              media="(max-width: 1540px)"
            />

            <source
              srcSet={insertTagsInCloudinaryUrl(
                image,
                `c_auto,ar_1.9,g_auto,w_iw,q_auto,f_auto`
              )}
              media="(min-width: 1920px)"
            />

            <img
              src={insertTagsInCloudinaryUrl(
                image,

                `c_auto,ar_2.31,g_auto,w_iw,q_auto,f_auto`
              )}
              alt={title}
              loading="eager"
              fetchpriority="high"
              className={`${styles.image}`}
              ref={imageRef}
            />
          </picture>
        </div>
      )}
      <div className={`${styles.data}`}>
        <h1 className={`${styles.title}`}>{title}</h1>
        <div className={`${styles.metadata}`}>
          <span>Created</span>
          {displayName && (
            <span className={`${styles.author}`}> for {displayName}</span>
          )}
          <span> on</span>
          {date && <span className={`${styles.date}`}> {date}</span>}
        </div>
        <div className={`${styles.actions}`}>
          <div className={`${styles.socialShareWrapper}`}>
            <SocialShare
              url={`${site}${router.asPath}`}
              direction="horizontal"
              styled={true}
              style="dropdown"
              config={socialShareConfig}
              ctaTitle="Create Your Own Guide"
              ctaLink="/travel-guides/create"
            />
          </div>
        </div>
      </div>
    </header>
  );
}

function HeaderBreadcrumbs({ destination }) {
  if (
    !destination ||
    !destination?.destination_details ||
    !destination?.destination_details?.region ||
    !destination?.destination_details?.region?.data ||
    !destination?.destination_details?.region?.data?.attributes ||
    !destination?.destination_details?.region?.data?.attributes?.regionName ||
    !destination?.destination_details?.region?.data?.attributes?.region
  ) {
    return null;
  }

  const destination_title = destination?.destination_title;

  const { region, regionName } =
    destination?.destination_details?.region?.data?.attributes;

  return (
    <Breadcrumbs
      breadcrumbs={[
        { name: regionName, link: `/destinations/${region}` },
        {
          name: destination_title || destination?.slug,
          link: `/destinations/${region}/${destination?.slug}`,
        },
      ]}
    />
  );
}

function TextHeader({
  country_short_code,
  destinationTitle,
  title,
  socialShareConfig,
  destination,
}) {
  const router = useRouter();

  return (
    <header className={`${styles.header}`}>
      <div className={`${styles.row} ${styles.row1}`}>
        <div className={`${styles.titleContainer}`}>
          <div className={`${styles.flagTitleWrapper}`}>
            <span className={`${styles.flagWrapper}`}>
              <ReactCountryFlag
                countryCode={country_short_code}
                svg
                alt={`The flag of ${destinationTitle}`}
                style={{
                  width: "33px",
                  height: "24px",
                  border: "1px solid black",
                  background: "black",
                }}
                title={`${destinationTitle} flag`}
              />
            </span>
            <h1 className={`${styles.title}`}>{title}</h1>
          </div>

          <HeaderBreadcrumbs destination={destination} />
        </div>

        <div className={`${styles.actions}`}>
          <div className={`${styles.socialShareWrapper}`}>
            <SocialShare
              url={`${site}${router.asPath}`}
              direction="horizontal"
              styled={true}
              style="dropdown"
              config={socialShareConfig}
              ctaTitle="Create Your Own Guide"
              ctaLink="/travel-guides/create"
            />
          </div>
        </div>
      </div>
      {/* <div className={`${styles.row} ${styles.row2}`}>
      </div> */}
    </header>
  );
}
